import { BASE_URL } from "@/config/global";
// import { deleteCookie, getCookie } from "cookies-next";
import { ErrorResponse } from "@/types/global";
import NetworkError from "@/utils/network-error";
import { notFound, redirect } from "next/navigation";
import { userActionOutsideOfComponent } from "@/global-store/user";
import { clearLoginCookie } from "@/utils/auth-cookie";

interface CustomRequestInit extends RequestInit {
  redirectOnError?: boolean;
  retry?: boolean;
}

const fetcher = async <T>(
  input: string | string[],
  init?: CustomRequestInit,
): Promise<T> => {
  const url = `${BASE_URL}${Array.isArray(input) ? input[0] : input}`;
  const res = await fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      // Authorization: getCookie("token") as string,
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let errorResponse: ErrorResponse;
    try {
      errorResponse = (await res.json()) as ErrorResponse;
    } catch {
      errorResponse = {
        message: "Something went wrong. Please try again.",
        status: res.status,
        statusCode: String(res.status),
        timestamp: new Date().toISOString(),
      };
    }
    // eslint-disable-next-line
    let errorMessage = errorResponse?.message;
    if (typeof errorMessage !== "string" && Array.isArray(errorMessage)) {
      const [first] = errorMessage;
      errorMessage = first;
    }
    if (typeof errorMessage !== "string") {
      errorMessage = "Something went wrong. Please try again.";
    }
    if (errorResponse?.params) {
      // eslint-disable-next-line prefer-destructuring
      const paramMsg = Object.values(errorResponse.params)?.[0]?.[0];
      if (paramMsg) errorMessage = paramMsg;
    }
    if (res.status === 401) {
      const isLoginRequest =
        typeof input === "string" && input.includes("auth/login");
      if (init?.redirectOnError === false || isLoginRequest) {
        throw new NetworkError(errorMessage, res.status, errorResponse?.params);
      }
      if (!init?.retry) {
        try {
          const refreshRes = await fetch(`${BASE_URL}users/refresh`, {
            method: "POST",
            credentials: "include",
          });
          if (refreshRes.ok) {
            return fetcher(input, { ...init, retry: true });
          }
        } catch (error) {
          // Fall through to logout
        }
      }
      userActionOutsideOfComponent({ user: null, me: null });
      if (typeof window !== "undefined") {
        clearLoginCookie();
      }
      // deleteCookie("token");
      return redirect("/");
    }
    if (res.status === 404) {
      return notFound();
    }
    throw new NetworkError(errorMessage, res.status, errorResponse?.params);
  }
  return res.json();
};

export default fetcher;

interface MutationRequestInit
  extends Omit<CustomRequestInit, "body" | "method"> {
  body?: unknown;
}

fetcher.post = async <T>(
  input: string,
  init?: MutationRequestInit,
): Promise<T> =>
  fetcher(input, {
    ...init,
    method: "POST",
    body: JSON.stringify(init?.body),
    headers: { "Content-Type": "application/json" },
  });

fetcher.put = async <T>(
  input: string,
  init?: MutationRequestInit,
): Promise<T> =>
  fetcher(input, {
    ...init,
    method: "PUT",
    body: JSON.stringify(init?.body),
    headers: { "Content-Type": "application/json" },
  });

fetcher.delete = async <T>(
  input: string,
  init?: MutationRequestInit,
): Promise<T> =>
  fetcher(input, {
    ...init,
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(init?.body),
  });
